/**
 * AssetUploadDropzone — drag-and-drop / click-to-browse zone for the Data
 * Plane catalog.
 *
 * Behaviour:
 *   1. User drops or picks one or more files.
 *   2. We hash each file in the browser (sha256:hex) so the server can short-
 *      circuit duplicates without paying for the bytes.
 *   3. We forward the prepared payload back to the parent via ``onPicked``;
 *      the parent owns the UX for "Configure Asset" (usage type + mounts)
 *      because that step is the same whether the user uploaded a file or
 *      pasted text.
 *
 * The component is intentionally headless about the network: hashing is
 * client-side, but the actual ``createUploadUrl`` / PUT / ``createAsset``
 * round-trip is the parent's job. That keeps this component reusable for
 * the org-level Data Plane (no network context) and for unit tests.
 */
import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { computeFileSha256 } from '../../utils/contentHash';

const DEFAULT_MAX_FILE_BYTES = 1024 * 1024 * 1024; // 1 GiB — matches server cap

/**
 * @param {{
 *   onPicked: (prepared: Array<{
 *     file: File,
 *     fileName: string,
 *     contentType: string,
 *     size: number,
 *     contentHash: string,
 *   }>) => void,
 *   maxFileBytes?: number,
 *   accept?: string,
 *   disabled?: boolean,
 *   className?: string,
 * }} props
 */
export default function AssetUploadDropzone({
  onPicked,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  accept,
  disabled = false,
  className = '',
}) {
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Allowed extensions, derived from the ``accept`` prop (the catalog passes the
  // same list the backend supports). The HTML ``accept`` attribute only filters
  // the file-PICKER dialog and is silently bypassed by drag-and-drop — so we
  // ALSO enforce here, on both paths, to reject e.g. a dropped ``.zip`` up front
  // with a clear error instead of creating a dead "Unsupported" asset. ``accept``
  // unset ⇒ no client-side type gate (backend still marks unsupported).
  const allowedExts = (accept || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.startsWith('.'));

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length) return;
      setBusy(true);
      setError(null);
      try {
        // Reject oversized files up front — saves the user from waiting on
        // a hash they cannot upload anyway.
        for (const f of files) {
          if (f.size > maxFileBytes) {
            throw new Error(
              `${f.name} is ${formatBytes(f.size)} (max ${formatBytes(maxFileBytes)}).`,
            );
          }
        }
        // Reject unsupported file TYPES up front (drag-and-drop bypasses the
        // ``accept`` dialog filter). Mirrors the backend extractability gate so
        // the user gets an immediate, clear error rather than a silent dead row.
        if (allowedExts.length) {
          for (const f of files) {
            const name = String(f.name || '').toLowerCase();
            if (!allowedExts.some((ext) => name.endsWith(ext))) {
              throw new Error(
                `${f.name} isn't a supported type. Allowed: ${allowedExts.join(', ')}.`,
              );
            }
          }
        }
        const prepared = await Promise.all(
          files.map(async (file) => ({
            file,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
            contentHash: await computeFileSha256(file),
          })),
        );
        onPicked?.(prepared);
      } catch (e) {
        setError(e?.message || 'Could not read file.');
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allowedExts derives
    // from the stable ``accept`` prop; re-deriving each render is fine and avoids
    // a memo. ``accept`` is included so the closure tracks prop changes.
    [maxFileBytes, onPicked, accept],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onChange = (e) => {
    handleFiles(e.target.files);
    // Reset so picking the same file twice still fires onChange.
    e.target.value = '';
  };

  return (
    <div
      data-testid="data-plane-dropzone"
      onDragEnter={(e) => {
        e.preventDefault();
        if (!disabled) setIsOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
        isOver
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 bg-gray-50/60 hover:border-blue-300'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`.trim()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <UploadCloud className="h-7 w-7 text-gray-400" aria-hidden />
      <div className="text-sm text-gray-700">
        <span className="font-medium text-blue-600">Click to browse</span>{' '}
        or drop files here
      </div>
      <div className="text-[11px] text-gray-500">
        Files are hashed in your browser so duplicates dedupe automatically.
      </div>
      {busy ? (
        <div className="text-[11px] text-blue-600">Hashing…</div>
      ) : null}
      {error ? (
        <div className="text-[11px] text-red-600" role="alert">
          {error}
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={onChange}
        data-testid="data-plane-file-input"
      />
    </div>
  );
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return `${n} B`;
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
